import { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Checkbox from '../ui/Checkbox';
import DatePicker from '../ui/DatePicker';
import CalendarCell from '../ui/CalendarCell';
import Table from '../ui/Table';
import Modal from '../ui/Modal';
import Skeleton from '../ui/Skeleton';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';

// Employee time off (PTO Phase 2), on src/ui (DESIGN rev 2.1 §3.1: Table for the ledger, Calendar cell for dates).
// Visible to any internal staff member with a pto_profile; not Admin/Manager-gated. Same calls as before.

const REASONS = ['Vacation', 'Personal Time', 'Medical', 'Family Related', 'Other'];
const CANCELLABLE = new Set(['Pending', 'Approved']);
const STATE_TONE = { Pending: 'info', Approved: 'ok', Taken: 'ok', Denied: 'danger' };
const ON_CALENDAR = new Set(['Pending', 'Approved', 'Taken']);
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function days(halfDays) {
  if (halfDays == null) return 'None';
  const d = halfDays / 2;
  return `${d} day${d === 1 ? '' : 's'}`;
}
const pad = n => String(n).padStart(2, '0');
function todayStr() { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function isFutureStart(startDate) { return startDate > todayStr(); }
function isPastStart(startDate) { return startDate < todayStr(); }
const toUTC = s => new Date(`${s}T12:00:00Z`);
const fromUTC = d => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
const addDays = (s, n) => { const d = toUTC(s); d.setUTCDate(d.getUTCDate() + n); return fromUTC(d); };
const fmtDate = s => toUTC(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
const ymdOf = v => (typeof v === 'string' ? v.slice(0, 10) : new Date(v).toISOString().slice(0, 10));

function emptyForm() {
  return { startDate: '', endDate: '', halfStart: false, halfEnd: false, reason: 'Vacation', supervisorName: '', note: '' };
}

// 6 weeks of YYYY-MM-DD starting on the Sunday on or before the 1st of `month` ('YYYY-MM').
function monthCells(month) {
  const first = toUTC(`${month}-01`);
  const start = addDays(`${month}-01`, -first.getUTCDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}
function shiftMonth(month, n) {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1, 12));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

export default function TimeOff() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(null); // { totalHalfDays, pastDated }
  const [pastConfirm, setPastConfirm] = useState(false);
  const [warn, setWarn] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [month, setMonth] = useState(() => todayStr().slice(0, 7));
  const [change, setChange] = useState(null);   // { request, description, reason, errors, saving }
  const formRef = useRef(null);
  const confirm = useConfirm();
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true); setLoadErr('');
    try {
      const { data } = await api.get('/api/pto/me');
      setData(data);
    } catch (e) {
      setLoadErr(clean(e.response?.data?.error) || 'Your time off did not load.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live total whenever the range/toggles change.
  useEffect(() => {
    if (!form.startDate || !form.endDate) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post('/api/pto/me/preview', {
          startDate: form.startDate, endDate: form.endDate, halfStart: form.halfStart, halfEnd: form.halfEnd,
        });
        if (!cancelled) setPreview(data);
      } catch {
        if (!cancelled) setPreview(null);
      }
    })();
    return () => { cancelled = true; };
  }, [form.startDate, form.endDate, form.halfStart, form.halfEnd]);

  const b = data?.balance || {};
  usePageHeader({
    title: 'Time off',
    meta: b.benefitYearEnd ? `Benefit year ends ${fmtDate(ymdOf(b.benefitYearEnd))}` : undefined,
  });

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setWarn(''); };

  // Calendar picking: the first click sets the start, the second sets the end (or moves the start if earlier).
  const pickDay = (date) => {
    setWarn('');
    setForm(f => {
      if (!f.startDate || f.endDate) return { ...f, startDate: date, endDate: '' };
      if (date < f.startDate) return { ...f, startDate: date };
      return { ...f, endDate: date };
    });
  };

  const resetForm = () => { setForm(emptyForm()); setEditingId(null); setPreview(null); setPastConfirm(false); setWarn(''); };

  const submit = async (e) => {
    e?.preventDefault();
    setWarn('');
    if (!form.startDate || !form.endDate) { setWarn('Choose a start and an end date.'); return; }
    const past = isPastStart(form.startDate);
    if (past && !pastConfirm) { setWarn('This date is in the past. Confirm below that you already took this time off.'); return; }
    setSubmitting(true);
    try {
      const body = { ...form, pastConfirmed: past ? pastConfirm : false };
      if (editingId) await api.put(`/api/pto/me/requests/${editingId}`, body);
      else await api.post('/api/pto/me/requests', body);
      toast(editingId ? 'Request updated.' : 'Request sent for approval.');
      resetForm();
      await load();
    } catch (err) {
      setWarn(clean(err.response?.data?.error) || 'The request did not save. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (r) => {
    setEditingId(r.id);
    setForm({
      startDate: r.startDate, endDate: r.endDate, halfStart: r.halfStart, halfEnd: r.halfEnd,
      reason: r.reason, supervisorName: r.supervisorName || '', note: r.note || '',
    });
    setPastConfirm(!!r.pastConfirmed);
    setWarn('');
    setMonth(r.startDate.slice(0, 7));
    formRef.current?.scrollIntoView({ block: 'start' });
  };

  const cancel = async (r) => {
    const ok = await confirm({
      title: 'Cancel this time-off request?',
      body: `${fmtDate(r.startDate)}${r.endDate !== r.startDate ? ` to ${fmtDate(r.endDate)}` : ''}, ${days(r.totalHalfDays)}. The days go back to your balance.`,
      confirmLabel: 'Cancel request', cancelLabel: 'Keep request',
    });
    if (!ok) return;
    try { await api.delete(`/api/pto/me/requests/${r.id}`); toast('Request cancelled.'); await load(); }
    catch (err) { toast(clean(err.response?.data?.error) || 'The request did not cancel. Try again.', { tone: 'danger' }); }
  };

  // A request whose start date has passed is locked: file a change request instead of editing.
  const openChange = r => setChange({ request: r, description: '', reason: '', errors: {}, saving: false });
  const submitChange = async (e) => {
    e?.preventDefault();
    const errors = {};
    if (!change.description.trim()) errors.description = 'Describe the change you need.';
    if (!change.reason.trim()) errors.reason = 'Say why the entry needs to change.';
    if (Object.keys(errors).length) { setChange(c => ({ ...c, errors })); return; }
    setChange(c => ({ ...c, saving: true }));
    try {
      await api.post(`/api/pto/me/requests/${change.request.id}/change-request`, { description: change.description.trim(), reason: change.reason.trim() });
      setChange(null);
      toast('Change request sent. An approver will review it.');
      await load();
    } catch (err) {
      setChange(c => ({ ...c, saving: false, errors: { form: clean(err.response?.data?.error) || 'The change request did not send. Try again.' } }));
    }
  };

  if (loading && !data) {
    return (
      <div className="ui ui-page">
        <div className="ui-region ui-region--pad ui-stack"><Skeleton width="30%" /><Skeleton width="60%" /><Skeleton variant="block" height={240} /></div>
      </div>
    );
  }

  if (loadErr && !data) {
    return <div className="ui ui-page"><ErrorState title="Time off did not load" message={loadErr} onRetry={load} /></div>;
  }

  if (data && data.enrolled === false) {
    return (
      <div className="ui ui-page">
        <div className="ui-region">
          <EmptyState message="You are not set up for time off yet. Ask an admin to set your hire date in Admin, then come back here to request days." />
        </div>
      </div>
    );
  }

  const requests = data?.requests || [];
  const pastSelected = form.startDate && isPastStart(form.startDate);
  const canEditThis = (r) => r.status === 'Pending' || (r.status === 'Approved' && isFutureStart(r.startDate));
  const today = todayStr();
  const cells = monthCells(month);
  const monthLabel = toUTC(`${month}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const inRange = d => form.startDate && (form.endDate ? d >= form.startDate && d <= form.endDate : d === form.startDate);
  const itemsOn = d => requests.filter(r => ON_CALENDAR.has(r.status) && r.id !== editingId && d >= r.startDate && d <= r.endDate)
    .map(r => ({ id: r.id, label: r.status }));

  const columns = [
    { key: 'dates', label: 'Dates', width: '30%', render: r => `${fmtDate(r.startDate)}${r.endDate !== r.startDate ? ` to ${fmtDate(r.endDate)}` : ''}${(r.halfStart || r.halfEnd) ? ', half day' : ''}` },
    { key: 'days', label: 'Days', render: r => <span className="ui-num">{days(r.totalHalfDays)}</span> },
    { key: 'reason', label: 'Reason', render: r => clean(r.reason) },
    { key: 'status', label: 'State', render: r => (
      <span className="ui-cluster" title={r.status === 'Denied' && r.denialReason ? clean(r.denialReason) : undefined}>
        {STATE_TONE[r.status] ? <Badge tone={STATE_TONE[r.status]}>{r.status}</Badge> : <span className="ui-text-3">{r.status}</span>}
        {r.status === 'Denied' && r.denialReason && <span className="ui-text-3">{clean(r.denialReason)}</span>}
      </span>
    ) },
    { key: 'actions', label: <span className="ui-sr">Actions</span>, width: '24%', render: r => (
      <span className="ui-cluster">
        {CANCELLABLE.has(r.status) && canEditThis(r) && <>
          <Button variant="quiet" onClick={() => startEdit(r)}>Edit</Button>
          <Button variant="quiet" onClick={() => cancel(r)}>Cancel</Button>
        </>}
        {(r.status === 'Taken' || (r.status === 'Approved' && !isFutureStart(r.startDate))) && (
          <Button variant="quiet" onClick={() => openChange(r)}>Request a change</Button>
        )}
      </span>
    ) },
  ];

  return (
    <div className="ui ui-page">
      <div className="ui-cols">
        {/* Request form */}
        <section className="ui-section" aria-labelledby="to-request" ref={formRef}>
          <h2 className="ui-section__title" id="to-request">{editingId ? 'Edit request' : 'Request time off'}</h2>
          <p className="ui-section__note">Click the first day, then the last day. Or type the dates below.</p>
          <form className="ui-region ui-region--pad ui-stack" onSubmit={submit}>
            <div className="ui-toolbar">
              <Button variant="quiet" iconOnly icon={ChevronLeft} label="Previous month" onClick={() => setMonth(m => shiftMonth(m, -1))} />
              <span className="ui-heading" aria-live="polite">{monthLabel}</span>
              <Button variant="quiet" iconOnly icon={ChevronRight} label="Next month" onClick={() => setMonth(m => shiftMonth(m, 1))} />
              <span className="ui-spacer" />
              <Button variant="quiet" onClick={() => setMonth(today.slice(0, 7))}>Today</Button>
            </div>
            <div className="ui-month">
              {DOW.map(d => <div key={d} className="ui-month__dow" aria-hidden="true">{d}</div>)}
              {cells.map(d => (
                <CalendarCell key={d} date={d} outside={d.slice(0, 7) !== month} today={d === today}
                  selected={!!inRange(d)} items={itemsOn(d)} max={1} onClick={() => pickDay(d)} />
              ))}
            </div>

            <div className="ui-cols ui-cols--tight">
              <div className="ui-stack ui-stack--tight">
                <DatePicker label="Start date" value={form.startDate} onChange={e => set('startDate', e.target.value)} />
                <Checkbox label="Half day (start)" checked={form.halfStart} onChange={e => set('halfStart', e.target.checked)} />
              </div>
              <div className="ui-stack ui-stack--tight">
                <DatePicker label="End date" value={form.endDate} min={form.startDate || undefined} onChange={e => set('endDate', e.target.value)} />
                <Checkbox label="Half day (end)" checked={form.halfEnd} onChange={e => set('halfEnd', e.target.checked)} />
              </div>
              <Select label="Reason" value={form.reason} onChange={e => set('reason', e.target.value)} options={REASONS} />
              <Input label="Supervisor (optional)" value={form.supervisorName} onChange={e => set('supervisorName', e.target.value)} placeholder="Who approved this?" />
            </div>
            <Input label="Note (optional)" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Anything your manager should know" />

            <p className="ui-p ui-dense" aria-live="polite">
              {preview
                ? <>Total: <span className="ui-num">{days(preview.totalHalfDays)}</span>{preview.totalHalfDays === 0 && ' (no working days in this range)'}</>
                : <span className="ui-text-3">Pick a date range to see the total.</span>}
            </p>

            {pastSelected && (
              <div className="ui-stack ui-stack--tight">
                <span><Badge tone="warn">Past date</Badge></span>
                <Checkbox label="Yes, I already took this time off" checked={pastConfirm} onChange={e => { setPastConfirm(e.target.checked); setWarn(''); }} />
              </div>
            )}

            {warn && <p className="ui-p ui-field__error" role="alert">{warn}</p>}

            <div className="ui-cluster">
              <Button type="submit" variant="primary" loading={submitting} disabled={!preview || preview.totalHalfDays === 0}>
                {editingId ? 'Save changes' : 'Submit request'}
              </Button>
              {editingId && <Button variant="quiet" onClick={resetForm}>Cancel edit</Button>}
            </div>
          </form>
        </section>

        {/* Balance */}
        <section className="ui-section" aria-labelledby="to-balance">
          <h2 className="ui-section__title" id="to-balance">Your balance</h2>
          {b.benefitYearEnd && <p className="ui-section__note">Unused days expire when the benefit year ends on {fmtDate(ymdOf(b.benefitYearEnd))}.</p>}
          <div className="ui-region ui-region--pad ui-stack">
            {b.status === 'First90' ? (
              /* New employee: the grant lands at the 90-day mark, so show the projected entitlement and the date. */
              <>
                <dl className="ui-kv ui-num">
                  <dt>Available to request</dt><dd>{days(b.availableToRequest)}</dd>
                  <dt>Projected entitlement</dt><dd>{days(b.projectedEntitlement)}</dd>
                  <dt>Pending</dt><dd>{days(b.pending)}</dd>
                  <dt>Approved, upcoming</dt><dd>{days(b.approvedUpcoming)}</dd>
                </dl>
                <p className="ui-p ui-dense ui-text-2">
                  As a new employee, {days(b.projectedEntitlement)} become available on {b.firstGrantDate || 'your eligibility date'} (your
                  90-day mark). Time off cannot start before then.
                </p>
              </>
            ) : (
              <>
                <dl className="ui-kv ui-num">
                  <dt>Available</dt><dd>{days(b.available)}</dd>
                  <dt>Granted</dt><dd>{days(b.granted)}</dd>
                  <dt>Taken</dt><dd>{days(b.taken)}</dd>
                  <dt>Approved, upcoming</dt><dd>{days(b.approvedUpcoming)}</dd>
                  <dt>Pending</dt><dd>{days(b.pending)}</dd>
                  {b.adjustments ? <><dt>Adjustments</dt><dd>{days(b.adjustments)}</dd></> : null}
                  {b.forfeited ? <><dt>Forfeited</dt><dd>{days(b.forfeited)}</dd></> : null}
                </dl>
                {b.available < 0 && (
                  <div className="ui-stack ui-stack--tight">
                    <span><Badge tone="danger">Negative balance</Badge></span>
                    <p className="ui-p ui-dense ui-text-2">This usually means past-dated time off went over your grant. Ask an admin to check it.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>

      {/* My requests */}
      <section className="ui-section" aria-labelledby="to-requests">
        <h2 className="ui-section__title" id="to-requests">My requests</h2>
        <div className="ui-region">
          <Table caption="My time-off requests" columns={columns} rows={requests}
            empty={{ message: 'No requests yet. Pick your days on the calendar above to send the first one.' }} />
        </div>
      </section>

      <Modal open={!!change} onClose={() => setChange(null)} title="Request a change"
        footer={<>
          <Button variant="quiet" onClick={() => setChange(null)}>Cancel</Button>
          <Button variant="primary" loading={change?.saving} onClick={submitChange}>Send change request</Button>
        </>}>
        {change && (
          <form className="ui-stack" onSubmit={submitChange}>
            <p className="ui-p ui-dense ui-text-2">
              {fmtDate(change.request.startDate)}{change.request.endDate !== change.request.startDate ? ` to ${fmtDate(change.request.endDate)}` : ''} has
              started, so it can no longer be edited. An approver will review the change.
            </p>
            <Textarea label="What needs to change" value={change.description} error={change.errors.description} data-autofocus
              onChange={e => setChange(c => ({ ...c, description: e.target.value, errors: { ...c.errors, description: null } }))} />
            <Textarea label="Why" value={change.reason} error={change.errors.reason}
              onChange={e => setChange(c => ({ ...c, reason: e.target.value, errors: { ...c.errors, reason: null } }))} />
            {change.errors.form && <p className="ui-p ui-field__error" role="alert">{change.errors.form}</p>}
          </form>
        )}
      </Modal>
    </div>
  );
}
